import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Col, Form, Row } from 'antd';
import { getFilter } from 'hooks/useFilter';
import { useRecoilValue } from 'recoil';
import { useApi } from 'hooks/useApi';
import UsersApi from 'libs/api/users';
import { useNavigate } from 'hooks/useNavigate';
import ErrorHelper from 'helpers/ErrorHelper';
import { Link } from 'react-router-dom';
import Button from 'components/button';
import { EyeOutlined } from '@ant-design/icons';
import { DeleteIcon, EditIcon } from 'icons';
import LinkWithParams from '../../../components/linkWithParams';

const api = new UsersApi();

const useTeam = ({ isMainPage = true, isEdit, isCustomers = false } = {}) => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const filterData = useRecoilValue(getFilter);
  const { t } = useTranslation();
  const [additionalFilters] = useState(
    isCustomers
      ? {
          by_role: 'customer',
        }
      : {},
  );
  const {
    singleItem,
    data,
    loading,
    onChangeTableFilter,
    onDelete,
    updateInProcess,
    setUpdateInProcess,
  } = useApi({
    resourceKey: 'users',
    singleResourceKey: 'user',
    api,
    isMainPage,
    additionalFilters: additionalFilters,
  });

  const columns = [
    {
      title: t('Azioni'),
      dataIndex: '',
      width: isCustomers ? 100 : 180,
      render: (_, row) => (
        <Row
          gutter={16}
          justify={'left'}
          style={{ minWidth: isCustomers ? 50 : 150 }}
          onClick={(e) => e.stopPropagation()}
        >
          <Col>
            <LinkWithParams to={`${row.id}/view`}>
              <Button type={'primary-white'} icon={<EyeOutlined />} />
            </LinkWithParams>
          </Col>
          {!isCustomers && (
            <>
              <Col>
                <Link to={`${row.id}/edit`}>
                  <Button icon={<EditIcon />} />
                </Link>
              </Col>
              <Col>
                <LinkWithParams to={`${row.id}/delete`}>
                  <Button icon={<DeleteIcon />} />
                </LinkWithParams>
              </Col>
            </>
          )}
        </Row>
      ),
    },
    {
      title: t('Nome'),
      dataIndex: 'name',
      sorter: true,
    },
    {
      title: t('Cognome'),
      dataIndex: 'surname',
      sorter: true,
    },
    {
      title: t('Email'),
      dataIndex: 'email',
      sorter: true,
    },
    {
      title: t('Numero telefono'),
      dataIndex: 'phone',
      sorter: true,
    },
    {
      title: t('Ruolo Permessi'),
      dataIndex: 'role',
      sorter: true,
      render: (data) => t(data),
    },
    // {
    //   title: t('Insegna/e'),
    //   dataIndex: 'shops',
    //   render: (data) => data?.map((i) => i.title)?.join(','),
    // },
  ];

  const filters = useMemo(() => {
    return isCustomers
      ? [
          {
            title: t('Cerca membro'),
            type: 'input',
            name: 'by_email',
            values: [],
          },
        ]
      : [
          {
            title: t('Cerca membro'),
            type: 'input',
            name: 'by_email',
            values: [],
          },
          {
            title: t('Filtro ruolo'),
            type: 'select',
            name: 'by_role',
            values: [
              { value: '', title: t('Selezioa ruolo') },
              ...(filterData.available_admin_roles.map((i) => ({
                value: i.value,
                title: t(i.title),
              })) || []),
            ],
          },
        ];
  }, [t, filterData, isCustomers]);

  const handleCreateOrUpdate = useCallback(
    async (values, id) => {
      setUpdateInProcess(true);
      try {
        const data = {
          user: {
            ...values,
          },
        };
        if (!id) {
          await api.create({ data });
        } else {
          await api.put(id, { data });
        }
        navigate('/team/list');
      } catch (e) {
        form.setFields(ErrorHelper.getErrorsForForm(e?.response?.data || {}));
      } finally {
        setUpdateInProcess(false);
      }
    },
    [form, navigate, setUpdateInProcess],
  );

  useEffect(() => {
    if (isEdit) {
      let fieldsValues = [];
      for (let key in singleItem) {
        switch (key) {
          case 'shops': {
            fieldsValues.push({
              name: 'shop_ids',
              value: singleItem[key]?.map((i) => i.id),
            });
            break;
          }
          default: {
            fieldsValues.push({
              name: key,
              value: singleItem[key],
            });
          }
        }
      }
      form.setFields(fieldsValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [singleItem, isEdit]);

  return {
    handleCreateOrUpdate,
    form,
    columns,
    filters,
    filterData,
    updateInProcess,
    data,
    loading,
    onChangeTableFilter,
    onDelete,
    singleItem,
  };
};

export default useTeam;
